import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { ArrowRight, Layers, Target, BarChart3, DollarSign, TestTube, ShieldCheck, CheckCircle2 } from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import { AnswerFirstSections } from "@/components/AnswerFirstSections";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SchemaScript from "@/components/SchemaScript";
const pageTitle = "Performance Max Agency London | Google Shopping & PMax";
const pageDescription = "Performance Max management with clean conversion data, strong assets and audience signals, then reporting that separates brand and prospecting demand.";
const pageHeading = "Performance Max Agency London: PMax Management That Separates Brand from New Demand";
const lockedAnswer = "As a Performance Max agency in London, Integrated.Social gives Google's automated campaigns clean conversion data, strong assets and clear audience signals, then checks where the spend really goes. We split brand from prospecting in reporting, so you can see whether new demand is being bought. Results depend on your data, offer and market.";
const answerBlock = `${lockedAnswer} It suits ecommerce and B2B advertisers already spending on PMax; book a free PMax audit on a 30-minute call.`;
const faqSummary = "These answers cover how Performance Max works, how to tell new demand from brand demand, how to stop brand cannibalization, which asset groups to build, how to measure when Google limits reporting, how long learning takes and what budget is needed. In short, clean conversion data and persona-led assets come first, brand and prospecting are reported apart, and results depend on your data, offer and market.";
const faqs = [
  {
    q: "What does a Performance Max agency do?",
    a: lockedAnswer,
  },
  {
    q: "How do I know Performance Max is creating new demand?",
    a: "To judge whether Performance Max is creating new demand, separate branded from prospecting conversions, keep a stable brand-search comparison and review the CRM stages behind PMax leads. A platform-reported conversion alone cannot prove incrementality. Use the available evidence to decide whether to adjust, pause or extend a test.",
  },
  {
    q: "How does Performance Max work and why does it need specialist management?",
    a: "Performance Max (PMax) is Google's automated campaign type across Search, Shopping, Display, YouTube, Gmail and Maps. It uses conversion data, assets and audience signals to help determine delivery. We review the inputs, separate brand from prospecting in reporting and use the available evidence to decide whether the structure should change."
  },
  {
    q: "How do you manage Performance Max without losing control of where budget goes?",
    a: "We focus on clean conversion tracking, themed asset groups, audience signals and the available brand exclusions and negative keyword controls. Audience signals are suggestions, not hard targeting restrictions; Performance Max may reach relevant people outside them. We review the available spend and conversion reporting and explain its limits rather than claiming full placement transparency or guaranteed control of delivery."
  },
  {
    q: "How do you prevent Performance Max from cannibalizing brand search and existing campaigns?",
    a: "Brand cannibalization is a risk to assess, not an assumed outcome of every Performance Max campaign. We review campaign-level brand exclusions, negative keyword controls and the available search-term evidence alongside existing Search campaigns. Branded and prospecting activity are reported separately where the data allows. A platform-reported conversion does not prove incremental revenue, and applying an exclusion does not guarantee that every conversion represents new demand."
  },
  {
    q: "What asset groups do you build for Performance Max and why does structure matter?",
    a: "We build themed asset groups for relevant buyer personas, product lines and funnel stages, with headlines, descriptions, images, videos and final URLs aligned to each theme. Audience signals guide the system but do not restrict delivery to that persona. Asset groups are creative collections within a campaign; they do not establish isolated audience, market-level budget or geographic boundaries. We use available reporting to review the structure rather than treating it as proof of audience-level performance."
  },
  {
    q: "How do you measure Performance Max performance when Google limits reporting transparency?",
    a: "We review the search-term, asset-group and audience reporting available in the account, noting any reporting limits rather than assuming full placement or creative-combination visibility. Where consent and platform rules permit, validated offline conversion imports from your CRM can connect attributed leads to qualified stages. Search and PMax comparisons can inform a test, but shared tracking or a platform-reported conversion alone does not prove incrementality."
  },
  {
    q: "Should we run Performance Max alongside Search campaigns or replace them?",
    a: "Performance Max can complement Search campaigns rather than automatically replacing them. Search provides keyword and match-type controls; PMax offers goal-based delivery across Google's inventory. We assess query intent, conversion evidence and the available reporting before choosing a structure. Validate keyword structures, match types, and negative lists before uploading changes to Google Ads. Budget allocation between them should be based on CPA and pipeline data, not assumptions."
  },
  {
    q: "How long does it take for Performance Max to exit the learning phase?",
    a: "Learning behavior varies with the account, conversion signal, budget and market. We start with clean conversion definitions, use relevant historical data where available and review performance before making material changes. The available evidence, rather than a fixed time or volume promise, determines the next decision."
  },
  {
    q: "What is the impact of Google AI Mode on Performance Max performance?",
    a: "Google AI features can change how a search-results page is presented. Review the real search-result layout, query intent and available performance data before changing campaigns. PPC, SEO and AEO should share clear buyer questions and landing-page evidence, but Google decides what appears in an AI feature and no citation or traffic effect is guaranteed."
  },
  {
    q: "Can Performance Max work for B2B companies with long sales cycles and offline conversions?",
    a: "Yes, but it requires careful conversion setup. PMax optimizes for the conversion signals you give it. For B2B with long sales cycles, this can mean importing offline conversions from your CRM (qualified leads, opportunities, closed deals), where consent and platform rules permit, rather than optimizing for form fills alone. We configure and validate the available enhanced conversions and offline conversion imports before assessing the campaign against those business-outcome signals. This setup does not guarantee qualified leads or revenue."
  },
  {
    q: "What budget do we need for Performance Max to be effective?",
    a: "The useful Performance Max budget depends on deal economics, conversion volume, existing data and market competitiveness. We assess those factors before recommending a test structure or a starting budget; no minimum spend guarantees an effective outcome."
  },
  {
    q: "How do you report on Performance Max in a way that shows business impact?",
    a: "We review cost per qualified lead, pipeline contribution and return on ad spend where your data supports them. We separate brand and prospecting activity in reporting and explain attribution limits. Asset-group reporting is evidence to review, not proof of isolated audience returns or the causal effect of a creative combination. Incrementality requires a suitable test; platform attribution alone cannot establish it. Impressions, clicks and CTR provide context rather than serving as the primary business result."
  }
];

export default function ServicePerformanceMax() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/performance-max-agency/#webpage",
        "url": "https://integrated.social/services/performance-max-agency/",
        "name": pageTitle,
        "headline": pageHeading,
        "abstract": answerBlock,
        "description": pageDescription,
        "isPartOf": { "@id": "https://integrated.social/#website" },
        "about": { "@id": "https://integrated.social/#organization" },
        "publisher": { "@id": "https://integrated.social/#organization" },
        "breadcrumb": { "@id": "https://integrated.social/services/performance-max-agency/#breadcrumb" },
        "author": { "@id": "https://integrated.social/modi-elnadi/#person" },
        "inLanguage": "en-US",
        "dateModified": "2026-11-02"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/performance-max-agency/#service",
        "name": "Performance Max Agency - Google PMax Management",
        "url": "https://integrated.social/services/performance-max-agency/",
        "provider": { "@id": "https://integrated.social/#organization" },
        "image": "https://integrated.social/media/performance-max-controls-2026-11-02-hero_85c21352.jpg",
        "serviceType": "Performance Max Campaign Management",
        "description": "Specialist Performance Max management for B2B and B2C brands covering asset group architecture, brand exclusion strategy, audience signal optimization, offline conversion imports, and cross-channel performance reporting.",
        "audience": [
          { "@type": "BusinessAudience", "audienceType": "B2B companies" },
          { "@type": "Audience", "audienceType": "B2C and ecommerce brands" }
        ],
        "areaServed": [
          { "@type": "Country", "name": "United Kingdom" },
          { "@type": "Country", "name": "United States" },
          { "@type": "Place", "name": "Global" }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Performance Max Services",
          "itemListElement": [
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Performance Max Campaign Setup & Architecture" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Asset Group Strategy & Creative Testing" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Brand Exclusion & Cannibalization Prevention" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Offline Conversion Import & CRM Integration" } },
            { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "PMax + Search Campaign Architecture" } }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/performance-max-agency/#faq",
        "isPartOf": { "@id": "https://integrated.social/services/performance-max-agency/#webpage" },
        "mainEntity": faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/performance-max-agency/#breadcrumb",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://integrated.social/" },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://integrated.social/services/" },
          { "@type": "ListItem", "position": 3, "name": "Performance Max Agency London", "item": "https://integrated.social/services/performance-max-agency/" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        locale="en-US"
        ogImage="https://integrated.social/media/performance-max-controls-2026-11-02-hero_85c21352.jpg"
        ogImageAlt="Conceptual Performance Max workspace with product feeds, creative assets, campaign controls and human review."
        ogImageType="image/jpeg"
        ogImageWidth={1600}
        ogImageHeight={900}
        description={pageDescription}
        canonical="https://integrated.social/services/performance-max-agency/"
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>

        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link href="/services/" className="hover:text-foreground transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-foreground font-medium">Performance Max Agency London</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
                Performance Max Agency · UK & Global
              </span>
              <h1 className="font-serif headline-xl mb-6">
                {pageHeading}
              </h1>
              <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-8">{answerBlock}</p>

              <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl">
              <div className="border border-[#FF8C00]/30 bg-[#FF8C00]/5 rounded-sm px-3 py-2 text-center"><div className="text-[#FF8C00] font-bold text-base">Brand split</div><div className="text-muted-foreground text-xs mt-0.5">Report existing demand separately</div></div>
              <div className="border border-[#00D4AA]/30 bg-[#00D4AA]/5 rounded-sm px-3 py-2 text-center"><div className="text-[#00D4AA] font-bold text-base">Clean inputs</div><div className="text-muted-foreground text-xs mt-0.5">Conversions, assets and audience signals</div></div>
              <div className="border border-border rounded-sm px-3 py-2 text-center"><div className="text-foreground font-bold text-base">Review</div><div className="text-muted-foreground text-xs mt-0.5">Decisions follow the available data</div></div>
            </div>
            <div className="flex flex-wrap gap-4">
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_pmax"); trackSectionCtaClick({ section_name: "service_pmax_hero", cta_text: "Book a free PMax audit", destination: "https://calendly.com/modielnadi/30min" }); }}>
                  Book a free PMax audit <ArrowRight className="w-4 h-4" />
                </a>
                <Link href="/services/ppc-performance-max/"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200">
                  Full PPC & Performance Max service
                </Link>
              </div>
            </div>
            <figure>
              <img
                src="/media/performance-max-controls-2026-11-02-hero_85c21352.jpg"
                alt="Conceptual Performance Max workspace with product feeds, creative assets, campaign controls and human review."
                className="w-full h-auto rounded-xl"
                width={1600}
                height={900}
                loading="eager"
              />
              <figcaption className="mt-3 text-sm text-muted-foreground leading-relaxed">Conceptual illustration of Performance Max inputs, controls and review. Brand and new-demand trays represent a reporting distinction, not guaranteed audience isolation or a client result.</figcaption>
            </figure>
          </div>
        </header>
        <AnswerFirstSections
          methodTitle="How we read Performance Max"
          method={<p>We separate branded from new-customer conversions, check that asset groups match distinct buyer personas, and compare results with brand exclusions on and off where volumes allow. Every finding comes with the data behind it and the confidence we have in it.</p>}
          fit={<><p><strong>For:</strong> B2B and B2C advertisers already running Performance Max who need to review where the budget goes.</p><p className="mt-2"><strong>Not for:</strong> brand-new accounts with no conversion history; start with search on <Link href="/services/ppc-performance-max/" className="underline underline-offset-4">the PPC and Performance Max service</Link>.</p></>}
          evidence={<p><strong>Future objective:</strong> Clear brand-versus-prospecting reporting. Results are reported from the data available; no outcome is promised.</p>}
        />

        {/* The Problem with PMax */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">Why Performance Max campaigns can underperform</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              PMax uses the conversion goals and inputs supplied. We review whether those inputs reflect the business outcomes you want to measure.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: ShieldCheck,
                  title: "Brand cannibalization",
                  desc: "Branded queries can complicate incrementality assessment. We review brand exclusions and the available evidence rather than assuming every branded conversion would have happened without advertising."
                },
                {
                  icon: Layers,
                  title: "Single asset group structure",
                  desc: "A broad asset group can make themes harder to review. We organize relevant assets by persona, product or funnel stage without treating asset groups as isolated audience or budget boundaries."
                },
                {
                  icon: BarChart3,
                  title: "Optimizing for the wrong signal",
                  desc: "PMax optimizes toward the chosen conversion goals. Form fills alone may not represent qualified pipeline; we validate the available business-outcome signals before assessing performance."
                },
                {
                  icon: Target,
                  title: "No audience signals",
                  desc: "Audience signals are optional suggestions, not hard targeting restrictions. We review relevant signals for B2B and B2C while recognizing that PMax may reach people outside them."
                },
                {
                  icon: DollarSign,
                  title: "Underfunded learning phase",
                  desc: "Learning behavior depends on the account, conversion signal, budget and market. No fixed conversion count, learning duration or starting budget guarantees an effective outcome."
                },
                {
                  icon: TestTube,
                  title: "No creative testing framework",
                  desc: "PMax uses assets in automated delivery. We review themed assets and refresh decisions using the available evidence; a testing framework does not guarantee improved performance."
                }
              ].map((item, i) => (
                <div key={i} className="p-6 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                  <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="Performance Max management for B2B and B2C brands. We review conversion data, themed assets and audience signals, then report brand and prospecting separately where the available data allows. Audience signals are suggestions, not hard targeting restrictions. Results depend on your data, offer and market."
            title="Performance Max Agency | PMax for B2B and B2C"
            readingTime={5}
          />
        </div>

        {/* How We Manage PMax */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">How we manage Performance Max</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-12">
              The structural decisions we review against conversion evidence and your business goals.
            </p>
            <div className="grid md:grid-cols-2 gap-10">
              {[
                {
                  step: "01",
                  title: "Asset group architecture",
                  desc: "We build separate asset groups for each buyer persona, product line, and funnel stage. Each group has its own audience signals, headlines, descriptions, images, and videos aligned to that specific intent. These themed creative collections do not isolate audiences, geography or budgets; we interpret the available reporting with those limits in mind."
                },
                {
                  step: "02",
                  title: "Brand exclusion strategy",
                  desc: "We review campaign-level brand exclusions, negative keyword controls and existing brand Search campaigns, then check the available search-term evidence for brand variants. These controls support a test; they do not prove that all remaining delivery is prospecting."
                },
                {
                  step: "03",
                  title: "Offline conversion imports",
                  desc: "Where consent and platform rules permit, we configure and validate offline conversion imports from your CRM. Qualified leads, opportunities and closed deals can inform the chosen conversion goals rather than relying on form fills alone. This setup does not guarantee qualified leads or revenue."
                },
                {
                  step: "04",
                  title: "Audience signal strategy",
                  desc: "Where consent and platform rules permit, we use first-party customer data and relevant audience suggestions. These signals guide PMax but do not restrict delivery to your ICP or establish a cross-platform audience export."
                },
                {
                  step: "05",
                  title: "PMax + Search architecture",
                  desc: "We run PMax alongside Search campaigns with shared conversion tracking. Search campaigns handle your highest-intent, highest-converting keywords with exact match control. PMax can support shopping and cross-channel reach; we assess prospecting evidence separately. Budget allocation is based on CPA and pipeline data, reviewed weekly."
                },
                {
                  step: "06",
                  title: "Performance reporting",
                  desc: "We review cost per qualified lead, pipeline contribution and ROAS where your data supports them. We explain asset-group reporting and attribution limits; incrementality requires a suitable test. Weekly reviews inform asset strategy and budget decisions."
                }
              ].map((item, i) => (
                <div key={i} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <span className="font-serif text-5xl text-[#FF8C00]/30 font-bold leading-none">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PMax vs Search comparison */}
        <section className="bg-muted/30 py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">Performance Max vs Search: when to use each</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-12">
              The account, conversion evidence and commercial goals determine whether to use one or both campaign types.
            </p>
            <div className="overflow-x-auto border border-border rounded-xl">
              <table className="w-full min-w-[840px] text-sm">
                <thead className="bg-background"><tr><th className="px-5 py-4 text-left text-[#FF8C00]">Performance Max is best for</th><th className="px-5 py-4 text-left">Search campaigns are best for</th></tr></thead>
                <tbody className="divide-y divide-border text-muted-foreground">
                  {[
                    ["Discovery and prospecting - finding new customers outside your known keyword universe", "High-intent commercial queries - exact and phrase match control for your most valuable keywords"],
                    ["Shopping campaigns - product feed optimization across Search, Shopping, and Display", "Search-term reporting - review the available query data and its limits"],
                    ["Cross-channel presence - YouTube, Gmail, Display, and Maps alongside Search", "Competitor targeting - bidding on competitor brand terms with controlled messaging"],
                    ["Audience suggestions - relevant first-party signals where consent and platform rules permit", "Brand review - use appropriate Search structure and available exclusion controls"],
                    ["Audience inputs - signals may inform delivery but do not restrict it to existing visitors or contacts", "Testing new messaging - assess ad-copy evidence before changing budgets"],
                  ].map(([pmax, search]) => <tr key={pmax}><td className="px-5 py-4">{pmax}</td><td className="px-5 py-4">{search}</td></tr>)}
                </tbody>
              </table>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">Performance Max needs enough reliable conversion evidence for the account, market and commercial model. We assess the available data before recommending a test structure or starting budget.</p>
            <p className="mt-3 text-sm text-muted-foreground"><Link href="/services/ppc-performance-max/" className="text-[#FF8C00] hover:underline">PPC and Performance Max</Link> is the wider account page, not a second fee.</p>
          </div>
        </section>

        <section className="py-20"><div className="container"><h2 className="font-serif headline-lg mb-4">What we report</h2><p className="text-muted-foreground text-lg max-w-2xl">We separate brand and prospecting activity, show the conversion signals available and explain the confidence behind each decision. No result is promised.</p></div></section>
        {/* AI Mode impact */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
                  Google AI Mode & PMax
                </span>
                <h2 className="font-serif headline-lg mb-6">
                  Google AI Mode and where PMax ads may appear
                </h2>
                <p className="text-gray-400 text-lg leading-relaxed mb-6">
                  Google AI features can change how a search-results page is presented. We review observed layouts, query intent and the available campaign data rather than assuming a premium paid position or a predictable traffic effect.
                </p>
                <p className="text-gray-400 leading-relaxed mb-8">
                  PPC, SEO and AEO should share clear buyer questions and landing-page evidence. Google decides what appears in an AI feature, so we record what is observed and do not promise a citation or traffic effect.
                </p>
                <Link href="/services/google-ai-mode-optimisation/"
                  className="inline-flex items-center gap-2 text-[#FF8C00] hover:text-[#E07B00] font-medium transition-colors">
                  See our Google AI Mode Optimization service <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="space-y-4">
                {[{ label: "Brand and prospecting", value: "Split", note: "Report existing demand separately" }, { label: "Conversion evidence", value: "Check", note: "Review the signal used for optimization" }, { label: "Asset groups", value: "Match", note: "Keep personas and messages distinct" }, { label: "Decision record", value: "Review", note: "Explain the evidence and the next action" }].map((item) => <div key={item.label} className="flex items-center justify-between p-4 border border-white/10 rounded-lg"><div><div className="text-sm text-gray-400">{item.label}</div><div className="text-xs text-gray-600 mt-0.5">{item.note}</div></div><div className="text-2xl font-bold text-[#FF8C00] ml-4 flex-shrink-0">{item.value}</div></div>)}
              </div>
            </div>
          </div>
        </section>
        <section className="container max-w-5xl py-16"><h2 className="font-serif text-3xl md:text-4xl">How do you know Performance Max is creating new demand?</h2><p className="mt-4 max-w-3xl text-muted-foreground leading-relaxed">To judge whether Performance Max is creating new demand, separate branded from prospecting conversions, keep a stable brand-search comparison and review the CRM stages behind PMax leads. A platform-reported conversion alone cannot prove incrementality. Use the available evidence to decide whether to adjust, pause or extend a test.</p></section>
        {/* FAQ */}
        <section className="py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif headline-lg mb-4">Performance Max questions</h2>
            <p data-faq-summary className="text-muted-foreground text-lg mb-8">{faqSummary}</p>
            <p data-platform-source-context className="text-sm text-muted-foreground leading-relaxed mb-8">
              Platform context: Google's <a href="https://support.google.com/google-ads/answer/10724817?hl=en" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">Performance Max guidance</a> describes goal-based automation, not a client-results forecast. Its <a href="https://support.google.com/google-ads/answer/14530785?hl=en-AU" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">audience-signal guidance</a> explains that suggestions do not restrict delivery. The <a href="https://developers.google.com/google-ads/api/performance-max/asset-groups" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">asset-group documentation</a> describes themed creative collections within a campaign, not isolated audience or market budgets. These platform descriptions do not guarantee leads or return on spend.
            </p>
            <div className="space-y-2">
              <FaqAccordion
                  items={faqs}
                  headingLevel={3}
                  className="space-y-2"
                  itemClassName="border border-border rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-6 text-left hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-6 pb-6 text-muted-foreground leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-[#0d0d0d] text-white py-20">
          <div className="container">
            <div className="text-center mb-12">
              <h2 className="font-serif headline-lg mb-4">Three ways to work with us</h2>
              <p className="text-gray-400 text-lg max-w-xl mx-auto">
                Start where it makes sense for your business. No long contracts, no pitch decks.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="p-8 border border-[#FF8C00]/40 bg-[#FF8C00]/5 rounded-xl flex flex-col">
                <div className="text-xs font-mono uppercase tracking-widest text-[#FF8C00] mb-3">Free · No obligation</div>
                <h3 className="text-xl font-semibold mb-3">Free PMax Audit</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                  We review your Performance Max account structure, brand exclusion strategy, conversion tracking, and asset groups. You get a written report with specific fixes - whether you work with us or not.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-colors text-sm" onClick={() => { trackCalendlyClick("service_pmax"); trackSectionCtaClick({ section_name: "service_pmax_hero", cta_text: "Book a free PMax audit", destination: "https://calendly.com/modielnadi/30min" }); }}>
                  Book a free audit <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="p-8 border border-white/20 rounded-xl flex flex-col">
                <div className="text-xs font-mono uppercase tracking-widest text-gray-400 mb-3">Ongoing · Monthly retainer</div>
                <h3 className="text-xl font-semibold mb-3">PMax Management</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                  Full Performance Max management: asset group architecture, brand exclusions, audience signals, offline conversion imports, weekly reporting, and monthly strategy reviews.
                </p>
                <Link href="/services/ppc-performance-max/"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/30 text-white font-medium rounded-lg hover:bg-white/10 transition-colors text-sm">
                  Full PPC service <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="p-8 border border-white/20 rounded-xl flex flex-col">
                <div className="text-xs font-mono uppercase tracking-widest text-gray-400 mb-3">30 min · No pitch</div>
                <h3 className="text-xl font-semibold mb-3">Strategy call</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 flex-1">
                  Talk through your current PMax setup, what is and is not working, and what a restructured account could achieve. Honest assessment, no sales pressure.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-white/30 text-white font-medium rounded-lg hover:bg-white/10 transition-colors text-sm" onClick={() => { trackCalendlyClick("service_pmax"); trackSectionCtaClick({ section_name: "service_pmax_hero", cta_text: "Book a free PMax audit", destination: "https://calendly.com/modielnadi/30min" }); }}>
                  Book a call <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </section>
          <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="Performance Max Advertising"
          canonicalUrl="https://integrated.social/services/performance-max-agency/"
          excerpt="Performance Max campaigns managed for pipeline contribution, not just impressions and clicks."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
